import ExcelJS from 'exceljs';
import { branding } from 'stripes-config';
import {
  formatColumnName,
  formatCellValue,
  getColumnAlignment,
  getNumberFormat,
} from './excelFormatters';

export const exportToExcel = async (data, filename = 'circulation-log', servicePointsMap = {}, translations = {}, selectedColumns = [], columnMapping = {}) => {
  try {
    if (!data || data.length === 0) {
      throw new Error('No data to export');
    }

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'FOLIO Circulation Log';
    workbook.created = new Date();

    // Add cover sheet
    const coverSheet = workbook.addWorksheet('Cover Page');
    
    // Set column widths for centered layout
    coverSheet.columns = [
      { width: 8 }, { width: 15 }, { width: 20 }, { width: 25 },
      { width: 25 }, { width: 20 }, { width: 15 }, { width: 8 },
    ];

    // Header section with blue background
    for (let row = 10; row <= 22; row++) {
      for (let col = 3; col <= 6; col++) {
        const cell = coverSheet.getCell(row, col);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF106BA3' },
        };
      }
    }

    // Add logo if available - inside the blue box
    if (branding?.logo?.src) {
      try {
        const logoResponse = await fetch(branding.logo.src);
        const logoBuffer = await logoResponse.arrayBuffer();
        const logoId = workbook.addImage({
          buffer: logoBuffer,
          extension: 'png',
        });
        
        coverSheet.addImage(logoId, {
          tl: { col: 3.2, row: 10.8 },
          ext: { width: 180, height: 120 },
        });
      } catch (error) {
        console.warn('Could not load logo:', error);
      }
    }

    // Add text boxes for better alignment
    const platformTitle = translations.platformTitle || 'منصة الفوليو العربي';
    const moduleTitle = translations.moduleTitle || 'تقارير الإعارة';
    
    // Create merged cells for text areas
    coverSheet.mergeCells('C15:F16');
    const titleMergedCell = coverSheet.getCell('C15');
    titleMergedCell.value = platformTitle;
    titleMergedCell.font = { size: 32, bold: true, color: { argb: 'FFFFFFFF' } };
    titleMergedCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    
    coverSheet.mergeCells('C17:F18');
    const moduleMergedCell = coverSheet.getCell('C17');
    moduleMergedCell.value = moduleTitle;
    moduleMergedCell.font = { size: 16, color: { argb: 'FFE8F4FD' } };
    moduleMergedCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    // Report title
    const reportTitleCell = coverSheet.getCell('D24');
    reportTitleCell.value = filename;
    reportTitleCell.font = {
      size: 28,
      bold: true,
      color: { argb: 'FF106BA3' },
    };
    reportTitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    reportTitleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF8F9FA' },
    };

    // Information section
    const now = new Date();
    const infoData = [
      [translations.generatedDate || 'تاريخ التوليد:', now.toLocaleDateString()],
      [translations.generatedTime || 'وقت التوليد:', now.toLocaleTimeString()],
      [translations.totalRecords || 'إجمالي السجلات:', data.length.toLocaleString()],
      [translations.reportType || 'نوع التقرير:', translations.reportTypeValue || 'تصدير سجل الإعارة'],
      [translations.systemVersion || 'إصدار النظام:', 'FOLIO 2024.1'],
      [translations.generatedBy || 'مولد بواسطة:', translations.generatedByValue || 'نظام سجل الإعارة'],
    ];

    infoData.forEach((info, index) => {
      const row = 28 + index;

      // Label
      const labelCell = coverSheet.getCell(`C${row}`);
      labelCell.value = info[0];
      labelCell.font = { size: 14, bold: true, color: { argb: 'FF333333' } };
      labelCell.alignment = { horizontal: 'right', vertical: 'middle' };
      labelCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8F4FD' },
      };

      // Value
      const valueCell = coverSheet.getCell(`E${row}`);
      valueCell.value = info[1];
      valueCell.font = { size: 14, color: { argb: 'FF333333' } };
      valueCell.alignment = { horizontal: 'left', vertical: 'middle' };
      valueCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' },
      };
    });

    // Footer disclaimer
    const disclaimerCell = coverSheet.getCell('D36');
    disclaimerCell.value = translations.disclaimer || 'تقرير سري - يتم التعامل معه وفقاً لسياسات المؤسسة';
    disclaimerCell.font = {
      size: 12,
      italic: true,
      color: { argb: 'FF666666' },
    };
    disclaimerCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // Copyright
    const copyrightCell = coverSheet.getCell('D39');
    copyrightCell.value = translations.copyright || '© 2024 منصة الفوليو العربي لإدارة المكتبات';
    copyrightCell.font = { size: 11, color: { argb: 'FF999999' } };
    copyrightCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // Set row heights
    coverSheet.getRow(11).height = 40; // Logo space top
    coverSheet.getRow(12).height = 40; // Logo space middle
    coverSheet.getRow(13).height = 30; // Logo space bottom
    coverSheet.getRow(14).height = 20; // Small gap
    coverSheet.getRow(15).height = 35; // Main title part 1
    coverSheet.getRow(16).height = 35; // Main title part 2
    coverSheet.getRow(17).height = 25; // Module part 1
    coverSheet.getRow(18).height = 25; // Module part 2
    coverSheet.getRow(24).height = 45; // Report title
    for (let i = 28; i <= 33; i++) {
      coverSheet.getRow(i).height = 30;
    }
    coverSheet.getRow(36).height = 25;
    coverSheet.getRow(39).height = 20;

    const worksheet = workbook.addWorksheet('Circulation Log Data', {
      pageSetup: { paperSize: 9, orientation: 'landscape' },
    });

    const headers = selectedColumns.length > 0 ? selectedColumns : Object.keys(data[0]);
    const formattedHeaders = headers.map((header) => columnMapping[header] || formatColumnName(header));

    worksheet.addRow(formattedHeaders);

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF106BA3' },
    };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.height = 25;

    data.forEach((row, index) => {
      const rowData = headers.map((header) => {
        let value = row[header];
        
        if (header === 'servicePointId' && servicePointsMap[value]) {
          value = servicePointsMap[value];
        }
        
        return formatCellValue(value, header);
      });
      
      const dataRow = worksheet.addRow(rowData);

      if (index % 2 === 1) {
        dataRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF8F9FA' },
        };
      }

      dataRow.eachCell((cell, colNumber) => {
        const header = headers[colNumber - 1];
        
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        };

        cell.alignment = { horizontal: getColumnAlignment(cell.value, header) };

        const numFormat = getNumberFormat(cell.value, header);
        if (numFormat) {
          cell.numFmt = numFormat;
        }
      });
    });

    worksheet.columns.forEach((column, index) => {
      let maxLength = formattedHeaders[index].length;

      data.slice(0, 100).forEach((row) => {
        const value = row[headers[index]];
        if (value) {
          const displayLength = value.toString().length;
          if (displayLength > maxLength) {
            maxLength = displayLength;
          }
        }
      });

      column.width = Math.min(Math.max(maxLength + 3, 12), 60);
    });

    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    worksheet.autoFilter = {
      from: 'A1',
      to: `${String.fromCharCode(64 + headers.length)}1`,
    };

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = `FOLIO_${filename}_${timestamp}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename;
    link.click();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      filename: finalFilename,
      recordCount: data.length,
    };
  } catch (error) {
    console.error('Excel export error:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};